// TODO: remove all of this with https://paperlessparts.atlassian.net/browse/PP-22951
import { useState, useEffect } from 'react';
import type { Unsubscribable } from 'type-fest';

import { prepareAndMakeRequest } from '@frontend/api';
import type { Duration } from '@frontend/models';
import { durationFactory, DateTime } from '@frontend/models';
import { WaitUntilSetController, noop } from '@frontend/utils';

type PartFileUuid = string;
type FileProcessingStateSetter = (v?: FileProcessingState) => void;
//type DurationSetter = (v?: Duration) => void;
type DateTimeSetter = (v?: DateTime) => void;

type DataForPartFileUuid = {
  fileProcessingStateSetters: Set<FileProcessingStateSetter>;
  estimatedFinishTimeSetters: Set<DateTimeSetter>;
  fileProcessingState?: FileProcessingState;
  estimatedTime?: Duration;
  startTime?: DateTime;
  estimatedFinishTime?: DateTime;
};

//See `class EstimatedProcessingTimeView` in `webservices/parts/views.py`
export const enum FileProcessingState {
  QUEUED = 1, //still waiting in queue to be processing
  PROCESSING = 2, //processing started
  PROCESSED = 3, //file is done processing
}
const knownFileProcessingState = new Set<FileProcessingState>([
  FileProcessingState.QUEUED,
  FileProcessingState.PROCESSED,
  FileProcessingState.PROCESSING,
]);

export const getFileProcessingStep = (
  fileProcessingState: FileProcessingState,
) =>
  fileProcessingState === FileProcessingState.QUEUED
    ? 'waiting'
    : fileProcessingState === FileProcessingState.PROCESSING
    ? 'processing'
    : 'uploading';

export const getFileProcessingStepText = (
  fileProcessingState: FileProcessingState,
) =>
  fileProcessingState === FileProcessingState.QUEUED
    ? 'Waiting to process'
    : fileProcessingState === FileProcessingState.PROCESSING
    ? 'Processing'
    : 'Uploading';

type FileProcessingResponse = {
  uuid: PartFileUuid;

  status: FileProcessingState;

  //Note: The server sends its currentTime because the browser's clock may not be in sync with server
  //If status === FileProcessingState.PROCESSSING, then `currentTime - startTime` is the elapsed duration since processing started
  currentTime: string; //ISO 8601 string
  startTime: string; //ISO 8601 string

  //estimatedProcessingTime is a const and does not change over time. It is based on a linear regression of file type and file size against known historic processing times
  estimatedProcessingTime: number; //duration in milliseconds
};

class FileProcessingService {
  private cache = new Map<PartFileUuid, DataForPartFileUuid>();
  private processed = new Set<PartFileUuid>(); //cache of files that have been confirmed processed
  private nextController: WaitUntilSetController<void> =
    this.nextControllerFactory();
  private timeoutId?: ReturnType<typeof setTimeout>;
  private isActive = true;

  /*
   * estRemainder: Calculate finish time from now in minutes. (Rounded down)
   */
  public static estRemainder(estFinishedTime: DateTime) {
    return Math.floor(
      Math.max((estFinishedTime.value - Date.now()) / 60000, 0),
    );
  }

  constructor() {
    //Kick off async updater fn that polls for FileProcessingResponse on a special interval controlled by this.nextController
    this.updater();
  }

  public dispose() {
    this.isActive = false; //stops this.updater()
    this.clear();
    this.nextController?.set();
    clearTimeout(this.timeoutId);
    delete this.timeoutId;
  }

  public isProcessed(partFileUuid: PartFileUuid) {
    return this.processed.has(partFileUuid);
  }

  public getEstimatedFinishTime(
    partFileUuid: PartFileUuid,
  ): DateTime | undefined {
    return this.cache.get(partFileUuid)?.estimatedFinishTime;
  }

  public getStartTime(partFileUuid: PartFileUuid): DateTime | undefined {
    return this.cache.get(partFileUuid)?.startTime;
  }

  public getIsProcessing(partFileUuid: PartFileUuid): boolean {
    const data = this.cache.get(partFileUuid);
    return (
      !!data && data.fileProcessingState === FileProcessingState.PROCESSING
    );
  }

  public getIsWaiting(partFileUuid: PartFileUuid): boolean {
    const data = this.cache.get(partFileUuid);
    return (
      !!data &&
      (!data.fileProcessingState ||
        data.fileProcessingState === FileProcessingState.QUEUED)
    );
  }

  public getIsProcessed(partFileUuid: PartFileUuid): boolean {
    return this.processed.has(partFileUuid);
  }

  public subscribe({
    partFileUuid,
    fileProcessingStateSetter,
    estimatedFinishTimeSetter,
  }: {
    partFileUuid: PartFileUuid;
    fileProcessingStateSetter?: FileProcessingStateSetter;
    estimatedFinishTimeSetter?: DateTimeSetter;
  }): Unsubscribable {
    if (!fileProcessingStateSetter && !estimatedFinishTimeSetter) {
      //no actions for subscription, so subscription is not created and unsubscribe is noop
      return { unsubscribe: noop };
    }
    let data: DataForPartFileUuid | undefined = this.cache.get(partFileUuid);
    if (!data) {
      data = {
        fileProcessingStateSetters: new Set(),
        estimatedFinishTimeSetters: new Set(),
      };
      this.cache.set(partFileUuid, data);
    }
    const startingSettingCount =
      data.fileProcessingStateSetters.size +
      data.estimatedFinishTimeSetters.size;
    if (fileProcessingStateSetter) {
      if (data.fileProcessingState) {
        fileProcessingStateSetter(data.fileProcessingState);
      }
      if (data.fileProcessingState !== FileProcessingState.PROCESSED) {
        data.fileProcessingStateSetters.add(fileProcessingStateSetter);
      }
    }
    if (estimatedFinishTimeSetter) {
      if (data.estimatedFinishTime) {
        estimatedFinishTimeSetter(data.estimatedFinishTime);
      } else if (data.fileProcessingState !== FileProcessingState.PROCESSED) {
        data.estimatedFinishTimeSetters.add(estimatedFinishTimeSetter);
      }
    }
    if (startingSettingCount === 0) {
      //Trigger next update now because there is new partFileUuid to get updates for.
      //(And abort waiting for next update, if it was waiting)
      clearTimeout(this.timeoutId);
      delete this.timeoutId;
      this.nextController.set();
    }
    const cache = this.cache;
    return {
      unsubscribe: () => {
        //Re-get fileProcessingStateSetters, estimatedFinishTimeSetters because we don't want to hold on to reference if cache is cleared
        const data = cache.get(partFileUuid);
        if (data) {
          const { fileProcessingStateSetters, estimatedFinishTimeSetters } =
            data;
          fileProcessingStateSetters.delete(
            fileProcessingStateSetter as FileProcessingStateSetter,
          );
          estimatedFinishTimeSetters.delete(
            estimatedFinishTimeSetter as DateTimeSetter,
          );
          if (
            !fileProcessingStateSetters?.size &&
            !estimatedFinishTimeSetters?.size
          ) {
            cache.delete(partFileUuid);
          }
        }
      },
    };
  }

  public clear() {
    for (const {
      fileProcessingStateSetters,
      estimatedFinishTimeSetters,
    } of this.cache.values()) {
      fileProcessingStateSetters.clear();
      estimatedFinishTimeSetters.clear();
    }
    this.cache.clear();
    this.processed.clear();
  }

  private nextControllerFactory(reset?: boolean): WaitUntilSetController<void> {
    if (reset || !this.nextController) {
      this.nextController = new WaitUntilSetController();
    }
    return this.nextController;
  }

  //async updater fn that polls for estimatedTimeRemaining on a special interval controlled by this.nextController
  //* requests for status on each of the subscribed partFileUuids
  //* calls the applicable setters with data from the response. The setters are the setters from useState() hooks.
  private async updater() {
    //eslint-disable-next-line @typescript-eslint/no-this-alias
    const cache = this.cache; //capture for use inside function* which does not have access to `this`
    const processed = this.processed;
    while (this.isActive) {
      //Wait to
      await this.nextControllerFactory().waitUntilSet;

      //still active after wait?
      if (this.isActive) {
        this.nextControllerFactory(true); //reset

        //Compose uuids to inquire about
        const uuids: string[] = Array.from(this.unprocessedPartFileUuids());

        if (uuids.length) {
          const estimatedProcessingTimes = await prepareAndMakeRequest.post<
            FileProcessingResponse[]
          >('/parts/timings/file_processing', { body: { uuids } });

          //Update the startTime and estimatedProcessingTime and call each setter with the updates
          for (const {
            uuid,
            status,
            startTime,
            currentTime,
            estimatedProcessingTime,
          } of estimatedProcessingTimes) {
            const data = cache.get(uuid);
            if (data) {
              //Calculate the startTime if not defined yet
              //* Note startTime is the server's startTime. data.startTime is the startTime relative to this browser's clock.
              //  * The user's browser's machine's clock is not in sync with the server's clock. So an adjustment is made.
              //  * The server's `serverCurrentTime` was taken a moment before we measured `now`. Typically this would be 50-100ms for the time it takes
              //    the response to arrive and this code to execute. This is a small number. So the difference between `now and `serverStartTime` is the amount
              //    of time the server and browser's machine's clock are out of sync.
              if (startTime && !data.startTime) {
                try {
                  const serverCurrentTime = DateTime.fromIso(currentTime);
                  const serverStartTime = DateTime.fromIso(startTime);
                  const startTimeRelativeToBrowser = new DateTime(
                    Date.now() -
                      (serverCurrentTime.value - serverStartTime.value),
                  );
                  data.startTime = startTimeRelativeToBrowser;
                } catch {
                  //gracefully continue. Could not parse currentTime or startTime.
                }
              }

              //Set estimatedTime if not defined yet
              //Note, estimatedTime never changes once set. In future maybe backend will provide an updated/modified estimate. But today
              //the estimate is calculated once based on linear regression of file size and file type.
              if (
                !data.estimatedTime &&
                Number.isSafeInteger(estimatedProcessingTime) &&
                estimatedProcessingTime > 0
              ) {
                data.estimatedTime = durationFactory(estimatedProcessingTime);
              }

              //Set estimatedFinishTime if not defined yet and clear estimatedFinishTimeSetters because estimatedFinishTime is never modified once set
              if (
                !data.estimatedFinishTime &&
                data.estimatedTime &&
                data.startTime
              ) {
                data.estimatedFinishTime = data.startTime.add(
                  data.estimatedTime,
                );
                for (const estimatedFinishTimeSetter of data.estimatedFinishTimeSetters) {
                  estimatedFinishTimeSetter(data.estimatedFinishTime);
                }
                data.estimatedFinishTimeSetters.clear();
              }

              //Set fileProcessingState if it changed
              //And if the status reaches FileProcessingState.PROCESSED, clear the fileProcessingStateSetters and estimatedFinishTimeSetters because this was the final state
              if (
                knownFileProcessingState.has(status) &&
                data.fileProcessingState != status
              ) {
                data.fileProcessingState = status;
                for (const fileProcessingStateSetter of data.fileProcessingStateSetters) {
                  fileProcessingStateSetter(status);
                }
                if (status === FileProcessingState.PROCESSED) {
                  data.fileProcessingStateSetters.clear();
                  data.estimatedFinishTimeSetters.clear();
                  cache.delete(uuid);
                  processed.add(uuid);
                }
              }
            }
          }
        }

        //If there isn't a pending timeout, and atleast one unpressedPartFileUuid, then set next update in 5 seconds
        const [firstUnprocessedUuid] = this.timeoutId
          ? []
          : this.unprocessedPartFileUuids();
        if (firstUnprocessedUuid) {
          this.timeoutId = setTimeout(() => {
            this.nextController.set();
            delete this.timeoutId;
          }, 5000);
        }
      }
    }
  }

  private *unprocessedPartFileUuids() {
    for (const [
      partFileUuid,
      {
        fileProcessingStateSetters,
        estimatedFinishTimeSetters,
        fileProcessingState,
      },
    ] of this.cache.entries()) {
      if (
        fileProcessingState !== FileProcessingState.PROCESSED &&
        (fileProcessingStateSetters.size || estimatedFinishTimeSetters.size)
      ) {
        yield partFileUuid;
      }
    }
  }
}

export const fileProcessingService = new FileProcessingService();

/*
 * TODO: remove with https://paperlessparts.atlassian.net/browse/PP-22951
 * useFileProcessingState is a hook for the file processing state (queued, processing or processed)
 * @see FileProcessingState
 */
export const useFileProcessingState = (
  partFileUuid?: PartFileUuid,
  initState?: FileProcessingState,
): FileProcessingState | undefined => {
  const [fileProcessingState, fileProcessingStateSetter] = useState<
    FileProcessingState | undefined
  >(initState);

  useEffect(() => {
    if (!partFileUuid) {
      return;
    }
    if (fileProcessingService.isProcessed(partFileUuid)) {
      fileProcessingStateSetter(FileProcessingState.PROCESSED);
      return;
    }
    if (partFileUuid) {
      const { unsubscribe } = fileProcessingService.subscribe({
        partFileUuid,
        fileProcessingStateSetter,
      });
      return () => {
        unsubscribe();
      };
    }
  }, [partFileUuid]);
  return fileProcessingState;
};
